import { useCallback, useRef, useState } from "react";

import { parseCommentCompositeKey } from "../../utils/ratings/buildCommentMap";
import { normalizeComment, normalizeReadingFluency } from "../../utils/ratings/buildCommentPayload";

import { getReadingFluencyFromCustomData } from "../../const/fluencyCustomData";

/**
 * Manages the in-memory student subject (criteria-level) comment state
 * for the batch rating grid, including readingFluency on customData.
 *
 * Key design decisions:
 * - `comments` holds current UI values keyed by a composite string.
 *   HOMEROOM key:  `${studentId}_${gradingCriteriaId}`
 *   GROUPS key:    `${groupId}-${studentId}_${gradingCriteriaId}`
 *   In GROUPS mode, hydrate writes a key for every matching group membership
 *   (not just the first) so multi-group EDIT rows can resolve the comment.
 *   Edits and post-save sync fan out to every sibling key for the same
 *   student+criteria so a stale membership key cannot delta old text back.
 *
 * - `fluencyByKey` holds readingFluency WPM strings on the same keys.
 *
 * - `existingComments` holds the last server-side state (array from
 *   getGroupStudentCriteriaComments / getStudentCriteriaComments).
 *   Stored here so buildCommentPayload can diff current vs. server
 *   at save time without needing to re-fetch.
 *
 * - Save strategy is DELTA only — only changed records are sent.
 *   DO NOT switch to full-state like ratings.
 *
 * @param {"HOMEROOM"|"GROUPS"} params.mode
 */
const useCommentState = ({ mode }) => {
    const [comments, setComments] = useState({});
    const [fluencyByKey, setFluencyByKey] = useState({});
    const [existingComments, setExistingComments] = useState([]);
    const [hasCommentChanges, setHasCommentChanges] = useState(false);
    const commentsRef = useRef(comments);
    commentsRef.current = comments;
    const fluencyByKeyRef = useRef(fluencyByKey);
    fluencyByKeyRef.current = fluencyByKey;

    /**
     * Initializes comment + fluency UI state from the server response array.
     * In GROUPS mode, prefixes the key with `${groupId}-` so the key
     * matches the composite student key used by the rating grid.
     */
    const initializeFromServer = useCallback(
        (serverComments, groupData) => {
            const nextComments = {};
            const nextFluency = {};

            (serverComments ?? []).forEach((sc) => {
                const studentId = sc.gradingProfileStudentId;
                const gradingCriteria = sc.gradingCriteria;
                let key = `${studentId}_${gradingCriteria}`;

                if (mode === "GROUPS" && groupData?.length) {
                    // Write under every group membership so the EDIT row (and any
                    // other instance) can resolve the comment. First-match alone
                    // left multi-group students with a blank textarea.
                    const matchingGroups = groupData.filter((g) =>
                        (g.groupStudentContents ?? []).some(
                            (s) => s.gradingProfileStudentId?.toString() === studentId?.toString()
                        )
                    );
                    if (matchingGroups.length) {
                        matchingGroups.forEach((g) => {
                            const groupKey = `${g.groupId}-${studentId}_${gradingCriteria}`;
                            nextComments[groupKey] = sc.comment || "";
                            nextFluency[groupKey] = getReadingFluencyFromCustomData(sc.customData);
                        });
                        return;
                    }
                }

                nextComments[key] = sc.comment || "";
                nextFluency[key] = getReadingFluencyFromCustomData(sc.customData);
            });

            setComments(nextComments);
            setFluencyByKey(nextFluency);
            setExistingComments(serverComments ?? []);
            setHasCommentChanges(false);
        },
        [mode]
    );

    /**
     * Updates a comment cell. For GROUPS multi-membership keys, mirrors the new
     * value onto every sibling key for the same student+criteria.
     * @param {string} key   - composite comment key
     * @param {string} value - new comment text
     */
    const setComment = useCallback((key, value) => {
        const nextValue = value ?? "";
        setComments((prev) => {
            const next = { ...prev, [key]: nextValue };
            const { studentId, gradingCriteria } = parseCommentCompositeKey(key);
            if (!Number.isFinite(studentId) || !Number.isFinite(gradingCriteria)) {
                return next;
            }
            Object.keys(prev).forEach((otherKey) => {
                if (otherKey === key) return;
                const parsed = parseCommentCompositeKey(otherKey);
                if (
                    String(parsed.studentId) === String(studentId) &&
                    String(parsed.gradingCriteria) === String(gradingCriteria)
                ) {
                    next[otherKey] = nextValue;
                }
            });
            return next;
        });
        setHasCommentChanges(true);
    }, []);

    const setFluencyWpm = useCallback((key, value) => {
        const nextValue = value ?? "";
        setFluencyByKey((prev) => {
            const next = { ...prev, [key]: nextValue };
            const { studentId, gradingCriteria } = parseCommentCompositeKey(key);
            if (!Number.isFinite(studentId) || !Number.isFinite(gradingCriteria)) {
                return next;
            }
            // Mirror onto every sibling key (fluency map + comment map) so GROUPS
            // multi-membership rows stay consistent under buildCommentMap collapse.
            const siblingKeys = new Set([...Object.keys(prev), ...Object.keys(commentsRef.current)]);
            siblingKeys.forEach((otherKey) => {
                if (otherKey === key) return;
                const parsed = parseCommentCompositeKey(otherKey);
                if (
                    String(parsed.studentId) === String(studentId) &&
                    String(parsed.gradingCriteria) === String(gradingCriteria)
                ) {
                    next[otherKey] = nextValue;
                }
            });
            return next;
        });
        // Ensure a comment map entry exists so fluency-only creates can save.
        setComments((prev) => (Object.prototype.hasOwnProperty.call(prev, key) ? prev : { ...prev, [key]: "" }));
        setHasCommentChanges(true);
    }, []);

    /**
     * Updates the server-side baseline after a successful save.
     * Does NOT overwrite UI comments — mid-flight typing must be preserved.
     * Sibling-key consistency for GROUPS is owned by setComment / setFluencyWpm
     * fan-out and buildCommentMap collapse. Dirty flag is recomputed (comment +
     * fluency) so further edits after the save started stay eligible for
     * autosave / leave flush.
     *
     * @param {Array} serverComments - fresh server response
     */
    const syncFromServer = useCallback((serverComments) => {
        const payload = serverComments ?? [];
        setExistingComments(payload);

        const findExisting = (compositeKey) => {
            const { studentId, gradingCriteria } = parseCommentCompositeKey(compositeKey);
            return payload.find(
                (e) =>
                    String(e.gradingProfileStudentId) === String(studentId) &&
                    String(e.gradingCriteria) === String(gradingCriteria)
            );
        };

        const commentDirty = Object.entries(commentsRef.current).some(([compositeKey, comment]) => {
            const existing = findExisting(compositeKey);
            return normalizeComment(comment) !== normalizeComment(existing?.comment);
        });

        const fluencyDirty = Object.entries(fluencyByKeyRef.current).some(([compositeKey, wpm]) => {
            const existing = findExisting(compositeKey);
            return normalizeReadingFluency(wpm) !== getReadingFluencyFromCustomData(existing?.customData);
        });

        setHasCommentChanges(commentDirty || fluencyDirty);
    }, []);

    const resetComments = useCallback(() => {
        setComments({});
        setFluencyByKey({});
        setExistingComments([]);
        setHasCommentChanges(false);
    }, []);

    return {
        comments,
        fluencyByKey,
        existingComments,
        hasCommentChanges,
        initializeFromServer,
        setComment,
        setFluencyWpm,
        syncFromServer,
        resetComments,
        setHasCommentChanges
    };
};

export default useCommentState;
